import gql from 'graphql-tag';

import {assert} from 'Asserts';
import {
  ChemicalFormatType,
  ChemicalStructureInput,
  EMPTY_MOLFILE,
  ChemicalFileFormat,
} from 'chemistry/ChemistryConstants';
import {
  MarvinCompatibleFileFormat,
  MarvinChemicalFormat,
} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import {
  MoleculeCurrentVersionQuery,
  MoleculeCurrentVersionQueryVariables,
} from 'chemistry/molecules/__generated/MoleculeCurrentVersionQuery';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {getStore} from 'redux-stores/Store';
import {
  CreateAndRegisterBioentityArgs,
  CreateBioentityArgs,
} from 'registry/create-bioentity/CreateBioentityUtil';

import * as MoleculeDAO from './MoleculeDAO';

const MOLECULE_CURRENT_VERSION_QUERY = gql`
  query MoleculeCurrentVersionQuery($moleculeId: StrictString!) @skipAudit {
    molecule(id: $moleculeId) {
      ... on MoleculeNode {
        id
        currentVersion {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function onCurrentVersion(moleculeId: string, currentVersionId: string): boolean {
  return (
    currentVersionId === getStore().getState().ui.chemistry.moleculeVersionUIs[moleculeId].moleculeVersionId
  );
}

export async function getCurrentVersionId(moleculeId: string): Promise<string> {
  // This function assumes that the Apollo cache has been previously populated with moleculeId's
  // currentVersion.
  const result = await doQuerySafe<MoleculeCurrentVersionQuery, MoleculeCurrentVersionQueryVariables>(
    {query: MOLECULE_CURRENT_VERSION_QUERY},
    {variables: {moleculeId}, fetchPolicy: 'cache-only'}
  );
  assert(!isInaccessible(result.molecule), 'Molecule must be accessible.');
  return result.molecule.currentVersion.id;
}

export function chemicalStructureInputFromCreateBioentityArgs(
  args: CreateBioentityArgs | CreateAndRegisterBioentityArgs
): ChemicalStructureInput | null {
  if (args.chemicalSketcherValue === EMPTY_MOLFILE && args.smiles === '') {
    return null;
  } else if (args.chemicalSketcherValue === EMPTY_MOLFILE) {
    return {structureFormat: ChemicalFormatType.SMILES, value: args.smiles};
  } else {
    return {
      structureFormat: ChemicalFormatType.MOLFILE,
      value: args.chemicalSketcherValue ?? EMPTY_MOLFILE,
    };
  }
}

export async function stripSaltsAndNotify(
  structureFormat: ChemicalFormatType,
  chemicalStructure: string
): Promise<void> {
  const strippedMolecule = await MoleculeDAO.stripSalts(structureFormat, chemicalStructure);
  if (strippedMolecule.salts?.length > 0) {
    // TODO: Update this wording once we start automatically storing salts on a schema field.
    notifications.info(
      'Salt information was removed from the chemical structure you added. Please use schema fields to store salt information.'
    );
  }
}

export async function detectReaction(
  structure: string,
  format: MarvinCompatibleFileFormat
): Promise<boolean> {
  if (format !== ChemicalFileFormat.MRV) {
    const exported = await MoleculeDAO.molConvert(format, MarvinChemicalFormat.MRV, structure);
    structure = exported.structure!;
  }

  const doc = new DOMParser().parseFromString(structure, 'text/xml');
  if (doc.querySelector('reaction') || doc.querySelector('MReactionSign')) {
    return true;
  }
  return false;
}

export function formatMolecularFormula(formula: string): string {
  const subscriptMap: {[key: string]: string} = {
    '0': '₀',
    '1': '₁',
    '2': '₂',
    '3': '₃',
    '4': '₄',
    '5': '₅',
    '6': '₆',
    '7': '₇',
    '8': '₈',
    '9': '₉',
  };

  return formula.replace(/\d/g, (digit) => subscriptMap[digit]);
}
